import { PAGE_PATH } from '@/constants';
import {
  fetchActiveOrderByRegularOrder,
  fetchCheckProductCanCheckout,
  fetchEligibleShippingMethodsByOutrightPurchase,
  fetchEligibleShippingMethodsByRegularOrder,
  fetchFreeGiftByOrderId,
  fetchGetActiveCoupons,
  fetchMarkUpByOrder,
  fetchModifyCartItemCountByShoppingTrolley,
  fetchRegularOrderExtras,
  fetchSettlementShoppingCart,
  fetchGetGuessYouLikeProducts,
  fetchCalculateTime,
  fetchSetOrderDeliveryExpress,
  // 场景二优化：合并查询 API
  fetchCartInit,
  fetchCartExtras,
  extractCartExtrasParams,
} from '@/services/apis';
import { useAppStore, useUserStore } from '@/store';
import { PlaceOrderType, PromotionType, VirtualTargetType, LotteryActivityTimeSource, ProductExclusionGroupType, DeliveryType } from '@/types';
import type { ShoppingTrolley } from '@/types/global-business';
import { businessUtils } from '@/utils';
import NP from 'number-precision';
import { defineStore } from 'pinia';

interface State {
  /** 购物车、预下单卡片组数据 */
  shoppingTrolleyCardGroup: ShoppingTrolley.CardGroup[];
  /** 直接下单卡片组数据 */
  regularOrderCardGroup: ShoppingTrolley.CardGroup[];
  /** 购物车数据 */
  shoppingTrolley: AnyObject | null;
  /** 预下单数据 */
  outrightPurchase: AnyObject | null;
  /** 直接下单数据 */
  regularOrder: AnyObject | null;
  /** 场景二优化：购物车活动倒计时数据（从合并查询获取） */
  activityCountdownByCart: AnyObject | null;
  /** 购物车活动数据 */
  shoppingTrolleyPromotionResult: AnyObject | null;
  /** 预下单活动数据 */
  outrightPurchasePromotionResult: AnyObject | null;
  /** 直接下单活动数据 */
  regularOrderPromotionResult: AnyObject | null;
  /** 已售罄、失效的商品数据 */
  invalidCommodityList: ShoppingTrolley.Commodity[];
  /** 下单场景类型 */
  placeOrderType: PlaceOrderType;
  /** 当前订单的所有赠品 */
  freeGiftByOrder: AnyObject[];
  /** 当前订单的所有换购商品 */
  purchasePremiumByOrder: AnyObject[];
  /** 渲染的换购商品 */
  purchasePremiumCommodityList: AnyObject[];
  /** 优惠弹窗实例 */
  preferentailPopupInstance: AnyObject | null;
  /** 删除标识 */
  displayCardDelete: boolean;
  /** 选择的活动弹窗信息 */
  selectedCardGroupItem: ShoppingTrolley.CardGroup | null;
  // 已选择的购物车商品过滤掉定时开售的
  selectedCardNotWithPutOnSale: AnyObject[];
  /** 支付成功后的回调函数 */
  paySuccessCallback: Function | null;
  /** 下单留言 */
  leaveWordText: string;
  /** 下单地址 */
  shipAddress: AnyObject | null;
  /** 订阅提交数据 */
  subscribesSubmitParams: {
    subscriptionPlanId: string;
    periods: number;
    deliveryInterval: number;
    firstShippingDate: string | Date;
  };
  /** 订单中的商品是否参与了会员价活动 */
  whetherOrderContainMemberPrice: boolean;
  /** 订单是否使用了会员优惠（是否勾选） */
  whetherOrderUseMemberDiscount: boolean;
  /** 订单是否使用了购物金 */
  whetherOrderUseShoppingCredit: boolean;
  /** 猜你喜欢商品列表 */
  guessYouLikeProductConfig: {
    guessProducts: AnyObject[];
    introduce: string;
  };
  /** 当前显示猜你喜欢商品下标 */
  guessYouLikeProductCurrentIndex: number;
  /** 当前选中的猜你喜欢商品 */
  selectedGuessYouLikeProduct: AnyObject | null;
  /** 查询商品规格是否限购的结果数据 */
  canCheckoutResult: {
    canCheckout: boolean;
    reason: string;
    errorDetail?: Array<{
      type: ProductExclusionGroupType;
      skuIds: string[];
      message: string;
    }>;
  };
  regularSalePopupRef: AnyObject | null;
  /** 商品地区限制错误提示 */
  isProductRegionRestrictionError: boolean;
  /** 页面弹窗 */
  popupMap: {
    coupon: AnyObject | null;
  };
  /** 用户已获取的优惠券列表数据（包含可用、不可用） */
  userReceiveCoupons: AnyObject | null;
  /** 抽奖活动id */
  lotteryActivityId: string;
  /** 配送方式 */
  deliveryType: DeliveryType;
  /** 是否已点击支付按钮（用于判断是否需要重置配送方式） */
  hasClickedPayment: boolean;
  /** 是否处于支付流程中（H5 收银台会触发 visibilitychange，需据此跳过页面 onShow 的重复初始化） */
  isPaying: boolean;
  /** 场景二优化：缓存的限购校验结果（从合并查询获取） */
  _lastCheckProductCanCheckoutResult: AnyObject | null;
}

export const usePlaceOrderStore = defineStore('placeOrder', {
  state: (): State => ({
    shoppingTrolleyCardGroup: [],
    regularOrderCardGroup: [],
    shoppingTrolley: null,
    outrightPurchase: null,
    regularOrder: null,
    activityCountdownByCart: null,
    shoppingTrolleyPromotionResult: null,
    outrightPurchasePromotionResult: null,
    selectedCardNotWithPutOnSale: [],
    regularOrderPromotionResult: null,
    invalidCommodityList: [],
    freeGiftByOrder: [],
    purchasePremiumByOrder: [],
    purchasePremiumCommodityList: [],
    placeOrderType: PlaceOrderType.shoppingTrolley,
    preferentailPopupInstance: null,
    displayCardDelete: false,
    selectedCardGroupItem: null,
    paySuccessCallback: null,
    leaveWordText: '',
    shipAddress: null,
    subscribesSubmitParams: {
      subscriptionPlanId: '',
      periods: 0,
      deliveryInterval: 0,
      firstShippingDate: '',
    },
    whetherOrderContainMemberPrice: false,
    whetherOrderUseMemberDiscount: true,
    whetherOrderUseShoppingCredit: true,
    guessYouLikeProductConfig: {
      guessProducts: [],
      introduce: ""
    },
    guessYouLikeProductCurrentIndex: 0,
    selectedGuessYouLikeProduct: null,
    canCheckoutResult: {
      canCheckout: true,
      reason: '',
    },
    regularSalePopupRef: null,
    isProductRegionRestrictionError: false,
    popupMap: {
      coupon: null,
    },
    userReceiveCoupons: null,
    lotteryActivityId: '',
    deliveryType: DeliveryType.express,
    hasClickedPayment: false,
    isPaying: false,
    _lastCheckProductCanCheckoutResult: null,
  }),
  getters: {
    // 购物车商品是否全是定时开售或者失效
    isPutOnSaleOrEnabled(state) {
      const shoppingOnSale = state.shoppingTrolleyCardGroup
        .map((item) => item.commodityList)
        .flat()
        .filter(
          (item: any) =>
            item?._source?.productVariant.product.customFields.putOnSaleType === 'scheduled' &&
            new Date(item?._source.productVariant.product.customFields.putOnSaleTime).getTime() >
              new Date().getTime()
        );
      if (
        shoppingOnSale.length + state.invalidCommodityList.length ==
        useAppStore().shoppingTrolleyCount
      ) {
        return true;
      } else {
        return false;
      }
    },
    isRegularOrder(state) {
      return state.placeOrderType === PlaceOrderType.regularOrder;
    },
    /** 下单数据 */
    placeOrders(state): AnyObject | null {
      return this.isRegularOrder ? state.regularOrder : state.outrightPurchase;
    },
    /** 下单活动数据 */
    placeOrderPromotionResult(state): AnyObject | null {
      return this.isRegularOrder
        ? state.regularOrderPromotionResult
        : state.outrightPurchasePromotionResult;
    },
    /** 卡片组数据 */
    placeOrderCardGroup(state): AnyObject[] {
      return this.isRegularOrder ? state.regularOrderCardGroup : state.shoppingTrolleyCardGroup;
    },
    /** 是否全选 */
    isCheckedAll(state) {
      return state.shoppingTrolleyCardGroup
        .map((it) => it.commodityList)
        .flat()
        .filter((it) => !it.isCurrentSkuSoltOut)
        .filter(
          (it) =>
            !(
              it._source?.productVariant?.product?.customFields?.putOnSaleType &&
              new Date(it._source?.productVariant?.product?.customFields?.putOnSaleTime).getTime() >
                new Date().getTime()
            )
        )
        .every((it) => it.isChecked);
    },
    /** 已选择的商品数据 */
    selectedCommodityList(state): ShoppingTrolley.Commodity[] {
      const group = this.isRegularOrder
        ? state.regularOrderCardGroup
        : state.shoppingTrolleyCardGroup;

      return group
        .map((it) => it.commodityList)
        .flat()
        .filter((it) => it.isChecked);
    },
    /** 优惠类型详情 */
    discountByTypesList(): AnyObject[] {
      return businessUtils.placeOrder.getDiscounts(
        this.placeOrderPromotionResult?.promResult?.discountByTypes ?? []
      );
    },
    /** 购物金数据 */
    shoppingGold(): {
      total: number;
      shoppingCreditsDeduction: number;
      shoppingCreditsClaim: number;
      disableShoppingCredits: boolean;
    } {
      const promResult = this.placeOrderPromotionResult?.promResult;
      // 购物金抵扣
      const shoppingCreditsDeduction = promResult?.shoppingCreditsDeduction ?? 0;
      // 可得购物金
      const shoppingCreditsClaim = promResult?.shoppingCreditsClaim ?? 0;
      // 总金额
      const total = NP.plus(promResult?.orderTotalPrice ?? 0, shoppingCreditsDeduction);
      // 是否禁用购物金
      const disableShoppingCredits = promResult?.disableShoppingCredits ?? false;

      console.log(`购物金数据`, {
        total,
        shoppingCreditsClaim,
        shoppingCreditsDeduction,
        disableShoppingCredits,
      });

      return {
        total,
        shoppingCreditsClaim,
        shoppingCreditsDeduction,
        disableShoppingCredits,
      };
    },
    /** 优惠券信息 */
    couponConfig(): { amount: number; display: boolean } {
      const config = { amount: 0, display: false };
      const coupons = this.outrightPurchasePromotionResult?.promResult?.coupons ?? [];
      if (!coupons?.length) return config;
      const target = coupons.find((it: AnyObject) => it.selected);
      if (target?.price) {
        config.amount = NP.divide(target.price, 100);
        config.display = true;
      }
      return config;
    },
    /** 实付/合计 */
    totalActualPayment(): number {
      return this.placeOrders?.customFields?.orderPromotionResult?.promResult?.orderTotalPrice ?? 0;
    },
    /** 结算商品是否积分兑换优惠券商品 */
    isVirtualCouponType(): boolean {
      const item = this.selectedCommodityList[0];
      console.log(`isVirtualMemberType`, item);
      if (!item) return false;
      return (
        item._source.productVariant.customFields.virtualTargetType === VirtualTargetType.Coupon &&
        item.isPointsExchange
      );
    },
    // 用户可用优惠券列表数据
    receiveAvailableCouponList(state) {
      return state.userReceiveCoupons?.availableCoupons ?? [];
    },
    // 用户不可用优惠券列表数据
    receiveNotAvailableCouponList(state) {
      return state.userReceiveCoupons?.couponsAreNotAvailable ?? [];
    },
    // 用户不可用的兑换券列表数据
    lackOfExchangeGoodsCouponList(state) {
      const list = state.userReceiveCoupons?.lackOfExchangeGoodsCoupons ?? [];
      return list.map((it: AnyObject) => {
        it._unExchange = true;
        return it;
      });
    },
    /** 可以选择的猜你喜欢商品列表 */
    canGuessYouLikeProductList(state): AnyObject[] {
      let selectCommoditySkuIds: string[] = [];

      const group = this.isRegularOrder
        ? state.regularOrderCardGroup
        : state.shoppingTrolleyCardGroup;

      // 购物差下单时，需要过滤掉购物车的商品规格
      const commodityList = group
        .map((it) => it.commodityList)
        .flat();

      if (state.selectedGuessYouLikeProduct) {
        selectCommoditySkuIds = commodityList.map((item: AnyObject) => item.skuId).filter(item => item !== state.selectedGuessYouLikeProduct?.productVariant.id);
      } else {
        selectCommoditySkuIds = commodityList.map((item: AnyObject) => item.skuId);
      }
      
      const giftSkuIds: string[] = [];
      this.placeOrderCardGroup.forEach(ele => {
        ele.giftList.forEach((item: AnyObject) => {
          if (item.isAvailable) {
            if (Number(item.skuId)) {
              giftSkuIds.push(item.skuId);
            }
          }
        })
      })

      if (state.guessYouLikeProductConfig.guessProducts?.length) {
        console.log("猜你喜欢商品组", state.guessYouLikeProductConfig.guessProducts.filter(ele => !([...selectCommoditySkuIds, ...giftSkuIds].includes(ele.productVariant?.id))));
        return state.guessYouLikeProductConfig.guessProducts.filter(ele => !([...selectCommoditySkuIds, ...giftSkuIds].includes(ele.productVariant?.id))) || []
      }
      return [];
    }
  },
  actions: {
    /**
     * 修改购物车数量
     * @param orderLineId
     * @param count
     */
    async modifyShoppingTrolleyCount(orderLineId: string, count: number, callback?: () => void) {
      const res = await fetchModifyCartItemCountByShoppingTrolley(orderLineId, count);
      if (res) {
        callback?.();
        await this.exeShoppingTrolleyFlow();
        return res;
      }
    },
    /**
     * 修改分组勾选
     * @param index - 分组下标
     */
    async changeShoppingTrolleyCardGroupChecked(index: number) {
      const target = this.shoppingTrolleyCardGroup[index];

      if (target.isChecked) {
        // 已勾选
        target.commodityList.forEach((it) => (it.isChecked = false));
      } else {
        // 未勾选
        target.commodityList.forEach((it) => (it.isChecked = true));
      }

      // 反选自己
      target.isChecked = !target.isChecked;

      if (!this.displayCardDelete) {
        this.checkProductCanCheckout();
        return await this.exeShoppingTrolleyFlow();
      }
    },
    /**
     * 修改商品项勾选
     * @param index - 分组下标
     * @param cindex - 商品下标
     */
    async changeShoppingTrolleyCommodityItem(index: number, cindex: number) {
      const group = this.shoppingTrolleyCardGroup[index];
      const target = group.commodityList[cindex];

      // 反选
      target.isChecked = !target.isChecked;

      // 当前是否分组
      if (group.isGroup) {
        // 分组下的商品是否已经全选
        const isGroupChecked = group.commodityList.every((it) => it.isChecked);
        group.isChecked = isGroupChecked;
      }

      if (!this.displayCardDelete) {
        this.checkProductCanCheckout();
        return await this.exeShoppingTrolleyFlow();
      }
    },
    /** 全选 */
    async checkedAll() {
      this.shoppingTrolleyCardGroup.forEach((it) => {
        it.commodityList.forEach((cit) => {
          if (!cit.isCurrentSkuSoltOut) cit.isChecked = true;
        });
        it.isChecked = it.commodityList.every((it) => !it.isCurrentSkuSoltOut);
      });

      if (!this.displayCardDelete) {
        this.checkProductCanCheckout();
        return await this.exeShoppingTrolleyFlow();
      }
    },
    /** 取消全选 */
    async cancelCheckedAll() {
      this.shoppingTrolleyCardGroup.forEach((it) => {
        it.commodityList.forEach((cit) => {
          if (!cit.isCurrentSkuSoltOut) cit.isChecked = false;
        });

        // 管理场景下，反选需要把卡片组的勾选也去掉
        if (this.displayCardDelete) {
          it.isChecked = false;
        } else {
          it.isChecked = it.commodityList.every(
            (it) =>
              !it.isCurrentSkuSoltOut &&
              !(
                new Date(
                  it._source?.productVariant.product?.customFields?.putOnSaleTime
                ).getTime() > new Date().getTime() &&
                it._source?.productVariant.product?.customFields?.putOnSaleType == 'scheduled'
              )
          );
        }
      });

      if (!this.displayCardDelete) {
        this.checkProductCanCheckout();
        return await this.exeShoppingTrolleyFlow();
      }
    },
    /**
     * 查询商品规格是否限购
     * @returns
     */
    async checkProductCanCheckout() {
      try {
        const skuIds = this.selectedCommodityList
          .filter((it) => {
            const disabledItem =
              it.isCurrentSkuSoltOut ||
              !it.isCurrentSkuExist ||
              it.isTimedSale ||
              it.isProductPurchasePermission;
            if (!disabledItem) return it;
          })
          .map((it) => it.skuId);

        const skuQuantities = this.selectedCommodityList.map((f) => {
          return { skuId: f.skuId, quantity: f.count };
        });

        const res = await fetchCheckProductCanCheckout(skuIds, skuQuantities);

        if (res) {
          this.canCheckoutResult = res;

          // 更新购物车商品的限购标签
          this.updateRestrictionTags();
        }

        return res;
      } catch (error) {
        console.log(error);
      }
    },
    /**
     * 根据 errorDetail 更新购物车商品的限购标签
     */
    updateRestrictionTags() {
      const errorDetail = this.canCheckoutResult?.errorDetail || [];

      // 遍历购物车分组
      this.shoppingTrolleyCardGroup.forEach(cardGroup => {
        // 遍历商品列表
        cardGroup.commodityList.forEach(commodity => {
          // 查找匹配的错误信息
          const matchedError = errorDetail.find(error =>
            error.skuIds.includes(commodity.skuId)
          );

          if (matchedError) {
            commodity.restrictionTag = {
              type: matchedError.type,
              message: matchedError.message
            };
          } else {
            // 清空之前的标签
            commodity.restrictionTag = {
              type: null,
              message: ''
            };
          }
        });
      });

      // 同样更新 regularOrderCardGroup
      this.regularOrderCardGroup.forEach(cardGroup => {
        cardGroup.commodityList.forEach(commodity => {
          const matchedError = errorDetail.find(error =>
            error.skuIds.includes(commodity.skuId)
          );

          if (matchedError) {
            commodity.restrictionTag = {
              type: matchedError.type,
              message: matchedError.message
            };
          } else {
            commodity.restrictionTag = {
              type: null,
              message: ''
            };
          }
        });
      });
    },
    async exeShoppingTrolleyFlow(isMount: boolean = false, cartSetlment?: AnyObject | null[]) {
      if (!isMount) {
        const group = this.shoppingTrolleyCardGroup
          .map((it) => it.commodityList)
          .flat()
          .filter((it) => it.isChecked);

        // 根据本地已勾选状态重新丢进预下单
        const input = group.map((it) => ({
          productVariantId: it._source.productVariant.id,
          quantity: it.count,
        }));

        const input2: { productVariantId: any; quantity: any }[] = [];
        if (cartSetlment && cartSetlment?.length) {
          cartSetlment.forEach((item: AnyObject | null) => {
            group.forEach((item2: AnyObject) => {
              if (item?.productVariant.id === item2._source.productVariant.id) {
                input2.push({
                  productVariantId: item2._source.productVariant.id,
                  quantity: item?.quantity,
                });
              }
            });
          });

          // 进行结算
          await fetchSettlementShoppingCart(input2);
        } else {
          // 进行结算
          await fetchSettlementShoppingCart(input);
        }
      }

      // 获取数据，执行数据和界面更新
      const data = await this.getShoppingTrolley(true);

      return data;
    },
    async getShoppingTrolley(refresh?: boolean) {
      // 没有登录不调用获取数据
      if (!useUserStore().isLogin) return;

      if (!this.shoppingTrolleyCardGroup.length || refresh) {
        // ========== 场景二优化：使用合并查询 ==========
        // Step 2: CartInit（2 合 1：getShoppingCart + activityCountdownByCart）
        const cartInitRes = await fetchCartInit(
          false,
          this.whetherOrderUseMemberDiscount,
          this.whetherOrderUseShoppingCredit,
          !this.selectedGuessYouLikeProduct
        );

        if (cartInitRes?.getShoppingCart) {
          const getRes = cartInitRes.getShoppingCart;
          const shoppingTrolleyId = getRes.shoppingTrolley?.id;
          const outrightPurchaseId = getRes.outrightPurchase?.id;

          // 更新角标数量
          useAppStore().shoppingTrolleyCount = getRes.shoppingTrolley?.lines?.length ?? 0;

          // 保存活动倒计时数据（从合并查询获取）
          this.activityCountdownByCart = cartInitRes.activityCountdownByCart ?? null;

          // Step 3: CartExtras（4 合 1）- 从 getShoppingCart 提取参数
          const extrasParams = extractCartExtrasParams(getRes);

          let promotionResult: AnyObject[] | null = null;
          let checkProductCanCheckoutResult: AnyObject | null = null;

          if (extrasParams && shoppingTrolleyId) {
            const cartExtrasRes = await fetchCartExtras(
              extrasParams.orderIds,
              extrasParams.orderId,
              extrasParams.skuIds,
              extrasParams.skuQuantities
            );

            if (cartExtrasRes) {
              promotionResult = cartExtrasRes.orderPromotionResult;
              checkProductCanCheckoutResult = cartExtrasRes.checkProductCanCheckout;

              // 处理赠品数据
              if (cartExtrasRes.freeGiftByOrder) {
                this.freeGiftByOrder = cartExtrasRes.freeGiftByOrder;
              }

              // 处理换购数据
              if (cartExtrasRes.markUpByOrder) {
                let list: AnyObject[] = [];
                cartExtrasRes.markUpByOrder.forEach((it: AnyObject) => {
                  it.purchasePremiumProducts?.forEach((pp: AnyObject) => list.push(pp));
                });
                this.purchasePremiumByOrder = list;
              }

              // 处理优惠券数据（6合1新增）
              if (cartExtrasRes.getActiveCoupons) {
                this.userReceiveCoupons = cartExtrasRes.getActiveCoupons;
              }

              // 处理会员价数据（6合1新增）
              this.whetherOrderContainMemberPrice = cartExtrasRes.getOrderIsIncludeMemberPrice ?? false;
            }
          }

          const shoppingTrolleyPromotionResult = promotionResult?.find(
            (it: AnyObject) => it.promResult?.orderId === shoppingTrolleyId
          ) ?? null;
          const outrightPurchasePromotionResult = promotionResult?.find(
            (it: AnyObject) => it.promResult?.orderId === outrightPurchaseId
          ) ?? null;

          console.log(`活动数据`, {
            shoppingTrolley: getRes.shoppingTrolley,
            outrightPurchase: getRes.outrightPurchase,
            shoppingTrolleyPromotionResult,
            outrightPurchasePromotionResult,
          });

          this.shoppingTrolley = getRes.shoppingTrolley ?? null;
          this.outrightPurchase = getRes.outrightPurchase ?? null;

          this.shoppingTrolleyPromotionResult = shoppingTrolleyPromotionResult;
          this.outrightPurchasePromotionResult = outrightPurchasePromotionResult;

          // 保存限购校验结果供 checkProductCanCheckout 方法使用
          if (checkProductCanCheckoutResult) {
            this._lastCheckProductCanCheckoutResult = checkProductCanCheckoutResult;
          }

          let shoppingTrolleyCardGroup = [];
          if (this.placeOrderType === PlaceOrderType.shoppingTrolley) {
            shoppingTrolleyCardGroup = await businessUtils.placeOrder.getCartGroupCardConfig(
              getRes.shoppingTrolley,
              shoppingTrolleyPromotionResult,
              outrightPurchasePromotionResult,
              // 传递赠品和换购数据
              {
                freeGiftByOrder: this.freeGiftByOrder,
                purchasePremiumByOrder: this.purchasePremiumByOrder,
              }
            );
          } else {
            shoppingTrolleyCardGroup = await businessUtils.placeOrder.getCartGroupCardConfig(
              getRes.outrightPurchase,
              outrightPurchasePromotionResult,
              outrightPurchasePromotionResult,
              // 传递赠品和换购数据
              {
                freeGiftByOrder: this.freeGiftByOrder,
                purchasePremiumByOrder: this.purchasePremiumByOrder,
              }
            );
          }

          this.shoppingTrolleyCardGroup = shoppingTrolleyCardGroup.filter(
            (it) => it.isGroup === null || it.isGroup
          );

          this.invalidCommodityList = shoppingTrolleyCardGroup.map((it) => it.invalidList).flat();
          if (this.invalidCommodityList.length) {
            console.log(`getShoppingTrolley - 失效商品数据`, this.invalidCommodityList);
          }

          console.log(`商品卡片组数据（过滤group）`, [...this.shoppingTrolleyCardGroup]);
          // console.log(`商品卡片组数据（没有过滤group）`, [...shoppingTrolleyCardGroup]);
        }
      }

      return {
        shoppingTrolleyCardGroup: this.shoppingTrolleyCardGroup,
      };
    },
    /**
     * 获取直接下单的订单信息
     * 场景四优化：5合1 合并查询
     * - orderPromotionResult: 活动信息
     * - freeGiftByOrder: 赠品
     * - markUpByOrder: 换购
     * - getActiveCoupons: 优惠券列表
     * - getOrderIsIncludeMemberPrice: 会员价商品
     */
    async getRegularOrder(refresh: boolean = false) {
      // 没有登录，不调用直接下单活跃订单
      if (!useUserStore().isLogin) return;

      if (!this.regularOrder || refresh) {
        const regularOrder = await fetchActiveOrderByRegularOrder();
        if (regularOrder) this.regularOrder = regularOrder;

        // ===== 合并请求：活动信息 + 赠品 + 换购 + 优惠券 + 会员价（5 合 1） =====
        const extrasData = await fetchRegularOrderExtras(regularOrder.id);

        if (extrasData) {
          // 处理活动信息
          const regularOrderPromotionResult = extrasData.orderPromotionResult?.[0];
          this.regularOrderPromotionResult = regularOrderPromotionResult;

          // 处理赠品信息
          if (extrasData.freeGiftByOrder) {
            this.freeGiftByOrder = extrasData.freeGiftByOrder;
          }

          // 处理换购信息
          if (extrasData.markUpByOrder) {
            let list: AnyObject[] = [];
            extrasData.markUpByOrder.forEach((it: AnyObject) => {
              it.purchasePremiumProducts.forEach((pp: AnyObject) => list.push(pp));
            });
            this.purchasePremiumByOrder = list;
          }

          // 处理优惠券信息（5合1新增）
          if (extrasData.getActiveCoupons) {
            this.userReceiveCoupons = extrasData.getActiveCoupons;
          }

          // 处理会员价信息（5合1新增）
          this.whetherOrderContainMemberPrice = extrasData.getOrderIsIncludeMemberPrice ?? false;

          // 构建卡片组
          const regularOrderCardGroup = await businessUtils.placeOrder.getCartGroupCardConfig(
            regularOrder,
            regularOrderPromotionResult,
            regularOrderPromotionResult,
            {
              freeGiftByOrder: this.freeGiftByOrder,
              purchasePremiumByOrder: this.purchasePremiumByOrder,
            }
          );

          this.regularOrderCardGroup = regularOrderCardGroup.filter(
            (it) => it.isGroup === null || it.isGroup
          );
        }
      }

      return this.regularOrder;
    },
    async getFreeGiftByOrder(orderId: string, refresh?: boolean) {
      if (!this.freeGiftByOrder.length || refresh) {
        const data = await fetchFreeGiftByOrderId(orderId);
        if (data) this.freeGiftByOrder = data;
      }
    },
    async getMarkUpByOrder(orderId: string, refresh?: boolean) {
      if (!this.purchasePremiumByOrder.length || refresh) {
        const data = await fetchMarkUpByOrder(orderId);
        if (data) {
          let list: AnyObject[] = [];
          data.forEach((it: AnyObject) => {
            it.purchasePremiumProducts.forEach((pp: AnyObject) => list.push(pp));
          });
          this.purchasePremiumByOrder = list;
        }
      }
    },
    /** 获取优惠商品选择器的配置信息 */
    getPreferentialCommoditySelectorConfig(data: AnyObject) {
      const config = {
        title: '',
        subTitleOptions: {
          text: '',
          color: '',
          isAnimation: false,
        },
        scene: '',
        visible: false,
        canUnselect: false,
      };

      if (data.type === PromotionType.purchaseAtAPremium) {
        config.title = '优惠换购';
        config.subTitleOptions = { text: '勾选换购', color: '#ef6348', isAnimation: true };
        config.scene = 'purchasePremium';
        config.visible = true;
        config.canUnselect = true;
      } else if (
        [
          PromotionType.selectiveGift,
          PromotionType.fullDiscountPresent,
          PromotionType.actuallyPaid,
        ].includes(data.type)
      ) {
        config.title =
          data.type === PromotionType.selectiveGift
            ? '任选满赠'
            : data.type === PromotionType.actuallyPaid
            ? '实付满赠'
            : '满减送赠品';
        config.subTitleOptions = { text: '以下赠品无需付费', color: '#999897', isAnimation: false };
        config.scene = 'freeGift';

        // 根据活动类型判断是否渲染
        const promotionTypeToFlag = {
          [PromotionType.selectiveGift]:
            this.shoppingTrolleyPromotionResult?.isCartShowSelectiveGift,
          [PromotionType.fullDiscountPresent]:
            this.shoppingTrolleyPromotionResult?.isCartShowFullDiscountPresentGift,
          [PromotionType.actuallyPaid]:
            this.shoppingTrolleyPromotionResult?.isCartShowActuallyPaidGift,
        } as Record<PromotionType, boolean>;

        // 判断是否是订单确认页
        const pages = getCurrentPages();
        const currentPage = pages[pages.length - 1];
        const isOrderConfirmPage = `/${currentPage.route}` === PAGE_PATH.orderConfirm.route;

        // 如果是订单确认页，必需渲染；否则根据活动配置判断是否渲染
        config.visible = isOrderConfirmPage || promotionTypeToFlag[data.type as PromotionType];
      }

      return config;
    },
    /**
     * 设置物流信息
     * 场景四优化：后端 getActiveOrderByType 已自动设置默认配送方式，此方法保留用于获取可用配送方式列表
     * 不再调用 setShippingMethodByOrderType Mutation
     */
    async setShippingMethods() {
      // 后端已在 getActiveOrderByType 中自动设置默认配送方式
      // 这里只获取可用配送方式列表供展示使用（如果需要）
      if (this.placeOrderType === PlaceOrderType.regularOrder) {
        await fetchEligibleShippingMethodsByRegularOrder();
      } else {
        await fetchEligibleShippingMethodsByOutrightPurchase();
      }
    },
    /** 获取用户已获取的优惠券列表数据（包含可用、不可用） */
    async getUserReceiveCouponList(orderId: string, refresh?: boolean) {
      if (!this.userReceiveCoupons?.length || refresh) {
        this.userReceiveCoupons = await fetchGetActiveCoupons(orderId);
      }
      return this.userReceiveCoupons;
    },
    /** 获取猜你喜欢商品列表 */
    async getGuessYouLikeProductList(orderId: string) {
      this.guessYouLikeProductConfig = await fetchGetGuessYouLikeProducts(orderId);
    },
    /** 设置显示的猜你喜欢商品的下标 */
    async setGuessYouLikeProductCurrentIndex() {
      if (!this.canGuessYouLikeProductList?.length || this.guessYouLikeProductCurrentIndex + 1 >= this?.canGuessYouLikeProductList?.length) {
        this.guessYouLikeProductCurrentIndex = 0;
      } else {
        this.guessYouLikeProductCurrentIndex += 1;
      }
    },
    /**
     * 更新下单操作 - 优惠信息更变时重跑一遍预下单流程更新全部优惠信息
     * 场景四优化：移除 setShippingMethodByOrderType Mutation 调用
     * 后端已在 getActiveOrderByType 中自动设置默认配送方式
     *
     * 购物车场景优化（6合1）：
     * - 优惠券和会员价已在 exeShoppingTrolleyFlow 中通过 fetchCartExtras 获取
     * - 无需单独调用 getUserReceiveCouponList 和 fetchGetOrderIsIncludeMemberPrice
     *
     * 直接购买场景优化（5合1）：
     * - 优惠券和会员价已在 getRegularOrder 中通过 fetchRegularOrderExtras 获取
     */
    async updateHandler() {
      // 创建新的支付订单时 清空下单时的留言缓存
      this.leaveWordText = '';

      // 获取订单信息（内部已包含优惠券和会员价数据的获取）
      if (this.isRegularOrder) {
        // 直接购买场景：getRegularOrder 内部已包含 5合1（含优惠券+会员价）
        await this.getRegularOrder(true);
      } else {
        // 购物车/预下单场景：getShoppingTrolley 内部已包含 6合1（含优惠券+会员价）
        await this.getShoppingTrolley(true);
      }
    },
    /** 设置抽奖活动次数 */
    async setLotteryActivityCalculateTime(lotteryActivityId: string, source: LotteryActivityTimeSource) {
      if (!lotteryActivityId) return

      return await fetchCalculateTime({
        lotteryActivityId,
        source: source,
        sourceValue: '1',
      })
    },
    /** 重置操作 */
    async resetHandler() {
      // 只有未点击支付按钮时才重置配送方式（点击支付后无论成功或取消，都保留用户选择）
      if (!this.hasClickedPayment && this.deliveryType !== DeliveryType.express) {
        const orderId = this.placeOrders?.id;
        if (orderId) {
          try {
            await fetchSetOrderDeliveryExpress(orderId);
          } catch (error) {
            console.error('重置配送方式失败:', error);
          }
        }
      }
      // 重置前端配送方式状态
      this.deliveryType = DeliveryType.express;
      // 重置支付点击标志
      this.hasClickedPayment = false;

      // 重置支付流程标志
      this.isPaying = false;

      // 重置下单类型
      this.placeOrderType = PlaceOrderType.shoppingTrolley;

      // 清空直接下单数据
      this.regularOrder = null;
      this.regularOrderPromotionResult = null;
      this.regularOrderCardGroup = [];

      this.isProductRegionRestrictionError = false;

      // 清空购物车和预下单
      this.shoppingTrolleyCardGroup = [];

      this.selectedGuessYouLikeProduct = null;

      // ...

      // 更新数据
      this.getShoppingTrolley(true);
    },
  },
});
